import { ComponentType, FC, PropsWithChildren } from 'react'
import { ChevronDown, LucideProps } from 'lucide-react'
import { EventAttendeeStatus } from '@wellesley/openapi'
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@wellesley/ui'
import { Action } from 'components/ActionsDropdown'

export type AttendeeAction = Omit<Action, 'icon'> & {
  value?: EventAttendeeStatus
  icon?: ComponentType<LucideProps>
}

type AttendeeActionsProps = PropsWithChildren & {
  attendeeStatus?: EventAttendeeStatus | null | undefined
  actions: AttendeeAction[]
}

const ActionView: FC<AttendeeAction> = ({
  icon: ActionIcon,
  name,
  isSelected,
}) => (
  <>
    {ActionIcon && (
      <ActionIcon
        className={cn(
          'size-xxs flex-shrink-0',
          isSelected &&
            '[&:has(circle)_path]:text-gray-100 [&_circle]:text-gray-700',
        )}
        fill={isSelected ? 'var(--gray-700)' : 'transparent'}
      />
    )}
    {name}
  </>
)

// Grid
// DIRECT -> 1fr 1fr 1fr  flat | 1fr 1fr 48px dropdown
// PUBLIC -> 1fr 1fr 48px flat | 1fr 1fr 48px dropdown

export const AttendeeActions: FC<AttendeeActionsProps> = ({
  attendeeStatus,
  actions,
}) => {
  if (!actions?.length) {
    return null
  }

  const selectedAction = actions.find((action) => action.isSelected)
  const shouldShowFlat = !attendeeStatus || attendeeStatus === 'INVITED'

  return shouldShowFlat ? (
    <div
      className={cn('grid gap-xs', {
        'grid-cols-3': attendeeStatus === 'INVITED',
        'grid-cols-[1fr_1fr_48px]': !attendeeStatus,
      })}
      data-testid="attendee-actions-grid"
    >
      {actions.map((action) => (
        <Button
          key={action.name}
          size="m"
          className="min-w-0"
          variant="secondary"
          onClick={action.handler}
          disabled={action?.disabled || false}
          data-testid={`action-button-${action.name.toLowerCase()}`}
        >
          <ActionView {...action} />
        </Button>
      ))}
    </div>
  ) : (
    <div
      className="grid grid-cols-[1fr_1fr_48px] gap-xs"
      data-testid="attendee-actions-dropdown"
    >
      <DropdownMenu>
        <DropdownMenuTrigger
          className="
          [&>svg]:flex-shrink-0 [&>svg]:transform [&>svg]:transition-transform [&>svg]:duration-200
          [&[data-state=open]>svg:last-child]:rotate-180
        "
          asChild
        >
          {selectedAction && (
            <Button
              variant="secondary"
              disabled={selectedAction.disabled}
              data-testid="dropdown-trigger"
            >
              <ActionView {...selectedAction} />
              <ChevronDown className="-mx-xxxs" size={18} />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[240px]">
          <DropdownMenuRadioGroup value={selectedAction?.value}>
            {actions.map((action) => (
              <DropdownMenuRadioItem
                key={action.name}
                value={action.value || ''}
                onSelect={action.handler}
                disabled={action.disabled}
                className="cursor-pointer"
                data-testid={`dropdown-item-${action.name.toLowerCase()}`}
              >
                <ActionView {...action} />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
